import { useContext } from "react";
import useSmartContracts from "../../../hooks/useSmartContracts";
import useTokens from "../../../hooks/useTokens";
import { TokensContext } from "./TokensContext";
import useCompany from "../../../hooks/useCompany";

export default function useTokenSettings() {
  const { activeCompany: company } = useCompany();
  const context = useContext(TokensContext);
  const smartContractsApi = useSmartContracts();
  const tokensApi = useTokens();

  if (!context) {
    throw new Error("useTokenSettings must be used within a TokensProvider");
  }

  const toggleSmartContract = async (contractId) => {
    if (context.companyContracts[contractId] !== undefined) {
      await smartContractsApi.removeSmartContractFromCompany(company.id, contractId);
    } else {
      const enabledInNetwork = context.contracts.filter(
        (c) => context.companyContracts[c.id] !== undefined
      ).length;
      const order = enabledInNetwork + 1;
      await smartContractsApi.addSmartContractToCompany(company.id, contractId, order);
    }
    context.loadCompanySmartContracts();
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const enabledContracts = context.contracts
      .filter((contract) => context.companyContracts[contract.id] !== undefined)
      .sort((a, b) => context.companyContracts[a.id] - context.companyContracts[b.id]);

    const oldIndex = enabledContracts.findIndex(c => c.id === active.id);
    const newIndex = enabledContracts.findIndex(c => c.id === over.id);

    const updatedContracts = [...enabledContracts];
    const [movedContract] = updatedContracts.splice(oldIndex, 1);
    updatedContracts.splice(newIndex, 0, movedContract);

    const newCompanyContracts = { ...context.companyContracts };
    updatedContracts.forEach((contract, index) => {
      newCompanyContracts[contract.id] = index + 1;
    });

    context.setCompanyContracts(newCompanyContracts);

    const changedContracts = updatedContracts.filter(
      (contract) => context.companyContracts[contract.id] !== newCompanyContracts[contract.id]
    );
    await Promise.all(
      changedContracts.map((contract) =>
        smartContractsApi.updateCompanySmartContract(company.id, contract.id, newCompanyContracts[contract.id])
      )
    );
  };

  const getOrderedContracts = () => {
    const { contracts, companyContracts } = context;

    const withOrder = [];
    const withoutOrder = [];

    for (const contract of contracts) {
      if (companyContracts[contract.id] !== undefined) {
        withOrder.push(contract);
      } else {
        withoutOrder.push(contract);
      }
    }

    withOrder.sort((a, b) => companyContracts[a.id] - companyContracts[b.id]);

    return [...withOrder, ...withoutOrder];
  };

  const createToken = async (tokenData) => {
    const created = await tokensApi.createToken(tokenData);
    await context.reloadAll();
    if (created && !created.address && created.id) {
      context.trackDeployment(created.id);
    }
    return created;
  };

  const importToken = async (tokenData) => {
    const created = await tokensApi.createToken(tokenData);
    await context.reloadAll();
    if (created && !created.address && created.id) {
      context.trackDeployment(created.id);
    }
    return created;
  };

  const editToken = async (tokenId, tokenData) => {
    await tokensApi.updateToken(tokenId, tokenData);
    await context.reloadAll();
  };

  const deleteToken = async (tokenId) => {
    await tokensApi.deleteToken(tokenId);
    await context.reloadAll();
  };

  const isCompanyOwned = (contract) => {
    return contract.companyId === company?.id;
  };

  const generateContract = async (params) => {
    return await tokensApi.generateContract(params);
  };

  return {
    ...context,
    toggleSmartContract,
    handleDragEnd,
    getOrderedContracts,
    createToken,
    importToken,
    editToken,
    deleteToken,
    isCompanyOwned,
    generateContract,
  };
}
